import type { Dispatch, SetStateAction } from 'react';
import type { Advisor } from 'src/components/admin/advisor-select';
import { useId, useMemo, useRef, useState } from 'react';
import { DialogFooter, DialogHeader, DialogTitle } from '@savvy-kit/components/dialog';
import { Label } from '@savvy-kit/components/label';
import SearchableCombobox from '@savvy-kit/patterns/searchable-combobox';
import { toast } from 'sonner';
import { AdvisorSelect } from 'src/components/admin/advisor-select';
import TrackedButton from 'src/components/tracked/tracked-button';
import TrackedDialog from 'src/components/tracked/tracked-dialog';
import { graphql } from 'src/gql';

import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';

const MOVE_CLIENT_TO_ADVISOR = graphql(/* GraphQL */ `
  mutation MoveClientToAdvisor($input: MoveClientToAdvisorInput!) {
    moveClientToAdvisor(input: $input) {
      success
    }
  }
`);

/** Not a real group — picking it finds no matching advisor group, so the mutation gets a null `advisorGroupId`. */
const NO_ADVISOR_GROUP = 'None';

type EditAdvisorDialogProps = {
  readonly user_id: string;
  readonly current_advisor: Advisor;
  readonly setShowEditAdvisorDialog: Dispatch<SetStateAction<boolean>>;
};

export function EditAdvisorDialog({ setShowEditAdvisorDialog, user_id }: EditAdvisorDialogProps) {
  const [selectedAdvisor, setSelectedAdvisor] = useState<Advisor | null>(null);
  const [selectedAdvisorGroup, setSelectedAdvisorGroup] = useState<string | null>(null);
  const [selectedAdvisorGroupId, setSelectedAdvisorGroupId] = useState<string | null>(null);
  const advisorGroupFieldId = useId();
  const titleRef = useRef<HTMLHeadingElement>(null);

  const [moveClientToAdvisor] = useMutationWithErrorLogging<{
    moveClientToAdvisor: { success: boolean };
  }>(MOVE_CLIENT_TO_ADVISOR);

  const handleUpdateAdvisor = async () => {
    if (selectedAdvisor) {
      setSelectedAdvisor(selectedAdvisor);
      setShowEditAdvisorDialog(false);
      const loadingToast = toast.loading('Moving client to advisor...');
      try {
        const { data } = await moveClientToAdvisor({
          variables: {
            input: {
              clientUserId: user_id,
              newAdvisorUserId: selectedAdvisor.user.id,
              advisorGroupId: selectedAdvisorGroupId,
            },
          },
        });
        if (data?.moveClientToAdvisor.success) {
          toast.success('Client moved to advisor');
          window.location.reload();
        } else {
          toast.error('Failed to move client to advisor');
        }
      } catch (error) {
        console.error('Error moving client to advisor', error);
      } finally {
        toast.dismiss(loadingToast);
      }
    }
  };

  const setSelectedAdvisorAndResetAdvisorGroup = (advisor: Advisor | null) => {
    setSelectedAdvisor((prevState) => {
      if (prevState?.id !== advisor?.id) {
        setSelectedAdvisorGroup(null);
        setSelectedAdvisorGroupId(null);
      }
      return advisor;
    });
  };

  const advisorGroupNames = useMemo(
    () =>
      (selectedAdvisor?.advisorGroups ?? [])
        .filter((advisorGroup) => advisorGroup.representationType === 'group')
        .map((advisorGroup) => advisorGroup.name),
    [selectedAdvisor],
  );
  const advisorGroupOptions = useMemo(
    () => [NO_ADVISOR_GROUP, ...advisorGroupNames].map((name) => ({ value: name, label: name })),
    [advisorGroupNames],
  );

  return (
    <TrackedDialog
      elementName="form-dialog"
      open
      onOpenChange={(nextOpen) => !nextOpen && setShowEditAdvisorDialog(false)}
      initialFocus={titleRef}
    >
      <DialogHeader>
        <DialogTitle ref={titleRef} tabIndex={-1}>
          Edit Advisor
        </DialogTitle>
      </DialogHeader>
      <div className="flex flex-col gap-6">
        <AdvisorSelect
          onAdvisorSelected={setSelectedAdvisorAndResetAdvisorGroup}
          selectedAdvisor={selectedAdvisor}
          roleFilter="ADVISOR"
        />
        {advisorGroupNames.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={advisorGroupFieldId}>Select an advisor group</Label>
            <SearchableCombobox
              id={advisorGroupFieldId}
              options={advisorGroupOptions}
              value={selectedAdvisorGroup ?? ''}
              onValueChange={(value) => {
                const groupName = value === '' ? null : value;
                setSelectedAdvisorGroup(groupName);
                setSelectedAdvisorGroupId(
                  selectedAdvisor?.advisorGroups?.find((advisorGroup) => advisorGroup.name === groupName)?.id || null,
                );
              }}
              placeholder="Search advisor groups..."
              emptyMessage="No advisor groups found"
              clearable
            />
          </div>
        )}
      </div>
      <DialogFooter>
        <TrackedButton
          elementName="alert-actions-cancel-button"
          variant="secondary"
          onClick={() => setShowEditAdvisorDialog(false)}
        >
          Cancel
        </TrackedButton>
        <TrackedButton
          elementName="alert-actions-save-button"
          variant="default"
          disabled={!selectedAdvisor}
          onClick={handleUpdateAdvisor}
        >
          Save
        </TrackedButton>
      </DialogFooter>
    </TrackedDialog>
  );
}
